import type {
  DashboardAssistantCancelInput,
  DashboardAssistantEvent,
  DashboardAssistantSendInput,
  DashboardAssistantSendResult,
} from "@t3tools/contracts";
import { createContext, useContext, type ReactNode } from "react";

/**
 * Where the dashboard assistant's turns come from. The page only talks to
 * this interface: Storybook and tests provide a fixture source, and the live
 * assistant (over `/ws/t3`: `assistant.send`, `assistant.cancel` and the
 * `dashboard.assistant.event` push channel) provides the same shape.
 */
export interface DashboardAssistantSource {
  /** Starts a turn; its events arrive through `subscribe` carrying the returned `turnId`. */
  send(input: DashboardAssistantSendInput): Promise<DashboardAssistantSendResult>;
  cancel(input: DashboardAssistantCancelInput): Promise<void>;
  /** Every assistant event for this person; callers filter by `threadId`. */
  subscribe(listener: (event: DashboardAssistantEvent) => void): () => void;
}

const DashboardAssistantSourceContext = createContext<DashboardAssistantSource | null>(null);

/** Provides the assistant to dashboard pages. Without one, pages show no assistant. */
export function DashboardAssistantSourceProvider(props: {
  children: ReactNode;
  source: DashboardAssistantSource | null;
}) {
  return (
    <DashboardAssistantSourceContext.Provider value={props.source}>
      {props.children}
    </DashboardAssistantSourceContext.Provider>
  );
}

/** The provided assistant, or null when none is connected. */
export function useDashboardAssistantSource(): DashboardAssistantSource | null {
  return useContext(DashboardAssistantSourceContext);
}
